import { Button, DatePicker, Form, Input, InputNumber, Radio, Select } from "antd";
import dayjs from "dayjs";
import { Col, Row } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";

import "dayjs/locale/zh-cn";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { setDashboardFilter } from "@/redux/slices/dashboardV2.slice";

dayjs.extend(weekday);
dayjs.extend(localeData);

const TimeSelectorTab = ({ setFormData = () => {}, form }) => {
  const window = Form.useWatch("window", form);
  const xAxis = Form.useWatch("xAxis", form);
  const intervalTime = Form.useWatch("intervalTime", form);
  const comparisonPeriods = Form.useWatch("comparisonPeriods", form) || [{ color: "#e81416" }];
  const basePeriod = Form.useWatch(["comparisonPeriods", 0, "date"], form);

  const dispatch = useDispatch();
  const { dashboardFilter } = useSelector((state) => state.dashboardV2);

  const clearComparisonPeriods = () => {
    setFormData((prev) => ({ ...prev, window: undefined, comparisonPeriods: [{ color: "#e81416" }] }));
    form.setFieldValue("comparisonPeriods", [{}]);
    form.setFieldValue("window", undefined);
    dispatch(setDashboardFilter({ ...dashboardFilter, comparisonPeriods: [{ color: "#e81416" }] }));
  };

  const clearXAxis = () => {
    setFormData((pre) => ({ ...pre, xAxis: undefined }));
    form.setFieldValue("xAxis", undefined);
    dispatch(setDashboardFilter({ ...dashboardFilter, xAxis: undefined }));
  };

  const handleWindowChange = (e) => {
    const newWindow = e.target.value;
    const periods = form.getFieldValue("comparisonPeriods");

    if (periods && periods.length > 1 && periods[0]?.date) {
      const baseDate = periods[0].date;

      const updatedPeriods = periods.map((item, idx) => {
        if (idx === 0) return item;
        return {
          ...item,
          date: dayjs(baseDate).add(idx, newWindow).toISOString(),
        };
      });

      // ফর্ম আপডেট করুন
      form.setFieldsValue({ comparisonPeriods: updatedPeriods });

      // রিডাক্স আপডেট (যদি প্রয়োজন হয়)
      dispatch(
        setDashboardFilter({
          ...dashboardFilter,
          window: newWindow,
          comparisonPeriods: updatedPeriods,
        }),
      );
    }
  };

  return (
    <>
      <Row>
        <Col xs={6}>
          <Form.Item label="Select Interval" name="intervalTime">
            <Select
              showSearch={{ filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
              maxTagCount="responsive"
              placeholder="Select Interval"
              options={[
                { label: "Real Time", value: "" },
                { label: "Minute", value: 1 },
                { label: "Hour", value: 60 },
                { label: "Day", value: 1440 },
                { label: "Monthly", value: 43830 },
                { label: "Yearly", value: 525960 },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={6}>
          {!!intervalTime && (
            <Form.Item label="Interval Value" name="intervalValue">
              <InputNumber className="w-100" controls={false} placeholder="Enter Value" />
            </Form.Item>
          )}
        </Col>
      </Row>

      <Form.Item label="Select Operator" name="operator" className="w-100">
        <Select
          placeholder="Select Operator"
          style={{ width: "100%" }}
          disabled={intervalTime === ""}
          options={[
            { label: "Maximum", value: "max" },
            { label: "Minimum", value: "min" },
            { label: "Average", value: "avg" },
            { label: "Sum", value: "sum" },
          ]}
        />
      </Form.Item>

      <Form.Item
        label={
          <div>
            Select Time Period
            {xAxis && (
              <span onClick={clearXAxis} className="ml-2 inline-block text-danger h5" style={{ cursor: "pointer" }}>
                <RxCrossCircled />
              </span>
            )}
          </div>
        }
        name="xAxis"
        onDoubleClick={clearComparisonPeriods}
      >
        <Radio.Group className="d-flex" disabled={comparisonPeriods[0]?.date || comparisonPeriods.length > 1 || window}>
          <Radio.Button className="w-100 text-center" value="day">
            Daily
          </Radio.Button>
          <Radio.Button className="w-100 text-center" value="week">
            Weekly
          </Radio.Button>
          <Radio.Button className="w-100 text-center" value="month">
            Monthly
          </Radio.Button>
          <Radio.Button className="w-100 text-center" value="year">
            Yearly
          </Radio.Button>
          <Radio.Button className="w-100 text-center" value="custom">
            Custom
          </Radio.Button>
          {/* <Radio.Button className="w-100 text-center" value="lastXDays">
                Last X Days
              </Radio.Button> */}
        </Radio.Group>
      </Form.Item>

      {["custom"].includes(xAxis) && (
        <Form.Item label="Last X Days" name="lastXDaysValue">
          <InputNumber placeholder="Enter Last X Days Value" className="w-100" />
        </Form.Item>
      )}

      {/* {!xAxis && ( */}
      <div onDoubleClick={clearXAxis}>
        <Form.Item
          label={
            <div>
              Select Window
              {window && (
                <span onClick={clearComparisonPeriods} className="ml-2 inline-block text-danger h5" style={{ cursor: "pointer" }}>
                  <RxCrossCircled />
                </span>
              )}
            </div>
          }
          name="window"
        >
          <Radio.Group className="d-flex" disabled={xAxis} onChange={handleWindowChange}>
            <Radio.Button className="w-100 text-center" value="d">
              Daily
            </Radio.Button>
            <Radio.Button className="w-100 text-center" value="w">
              Weekly
            </Radio.Button>
            <Radio.Button className="w-100 text-center" value="M">
              Monthly
            </Radio.Button>
            <Radio.Button className="w-100 text-center" value="y">
              Yearly
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.List name="comparisonPeriods" initialValue={[{ color: "#e81416" }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, idx) => (
                <div key={key}>
                  <div className="d-flex align-items-center">
                    <label style={{ userSelect: "none" }} className="mt-2">
                      {idx === 0 ? "Base Period" : "Period " + idx}
                    </label>

                    {idx !== 0 && (
                      <RxCrossCircled className="ml-2" color="red" style={{ cursor: "pointer", fontSize: 16 }} onClick={() => remove(name)} />
                    )}
                  </div>

                  <Form.Item
                    {...restField}
                    name={[name, "date"]}
                    //  rules={[{ required: !xAxis, message: "Please select a date" }]}
                    getValueFromEvent={(date) => (date ? date?.toISOString() : null)}
                    getValueProps={(value) => ({
                      value: value ? dayjs(value) : null,
                    })}
                  >
                    <DatePicker
                      disabled={xAxis}
                      className="w-100"
                      placeholder="Select Time Period"
                      disabledDate={(current) => current && current > dayjs()}
                      format="YYYY-MM-DD"
                      style={{ borderColor: form.getFieldValue(["comparisonPeriods", name, "color"]) }}
                    />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "color"]} noStyle>
                    <Input hidden />
                  </Form.Item>
                </div>
              ))}

              {/* Add Period Button */}
              {fields.length < 7 && (
                <Form.Item className="mt-4">
                  <Button
                    type="dashed"
                    disabled={xAxis || !basePeriod}
                    onClick={() => {
                      const baseDate = form.getFieldValue(["comparisonPeriods", 0, "date"]);
                      let nextDate = "";

                      if (baseDate && window) {
                        // nextDate = dayjs(baseDate).add(fields.length, window).format("YYYY-MM-DD");
                        nextDate = dayjs(baseDate).add(fields.length, window).toISOString();
                      }

                      add({
                        date: nextDate ? nextDate : undefined,
                        color: rainbowColors[fields.length],
                      });
                    }}
                    block
                    icon={<FaPlus />}
                  >
                    Add Period
                  </Button>
                </Form.Item>
              )}
            </>
          )}
        </Form.List>
      </div>
    </>
  );
};

export default TimeSelectorTab;

export const rainbowColors = ["#e81416", "#ffa500", "#4b369d", "#faeb36", "#79c314", "#487de7", "#70369d"];

TimeSelectorTab.propTypes = {
  setFormData: PropTypes.func,
  form: PropTypes.any,
};
